import axios from 'axios';
import { ContextManager } from '../../context/manager';
import { getMarketContext } from '../../client/contextClient/contextQuery';
import { FilterDefinition, Filters, Params } from '../../types';
import {
  AppliedFilter,
  BrandEntity,
  BrandObject,
  ScoreCardData,
  HASH_VALIDITY_INTERVAL,
  OutputStructure,
  KpisDataObject,
  PricesObject,
} from './types';

const calculateTimestamp = (): number => {
  const currentTimestamp = Date.now();
  const roundedTimestamp = currentTimestamp - (currentTimestamp % HASH_VALIDITY_INTERVAL);

  return roundedTimestamp;
};

export const getHashedPayload = async data => {
  const timestamp = calculateTimestamp();
  const message = `${JSON.stringify(data)}.${timestamp}`;

  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(message);
  const hashBuffer = await crypto.subtle?.digest('SHA-256', dataBuffer);

  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
};

export async function sendInsightSummary(
  result: Record<string, unknown>,
  endpoint: string,
  getAccessToken: () => Promise<string>
) {
  const [token, hashedResult] = await Promise.all([getAccessToken(), getHashedPayload(result)]);
  const response = await axios.post(endpoint, result, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Payload-Hash': hashedResult,
    },
  });
  return response.data;
}

function isBrandEntity(obj: unknown): obj is BrandEntity {
  return typeof obj === 'object' && obj !== null && 'options' in obj && Array.isArray((obj as BrandEntity).options);
}

export async function fetchMyBrand(params: Params): Promise<string> {
  const res = await getMarketContext('/portfolio-bff/context/market/brands/brandOptions', params);

  if (!Array.isArray(res)) return '';

  const firstItem = res[0];
  if (!isBrandEntity(firstItem)) return '';

  const label = firstItem.options?.[0]?.label;
  return typeof label === 'string' ? label : '';
}

const getMarketTotal = (raw: ScoreCardData[]): Record<string, Record<string, number>> => {
  const totals = {};
  raw.forEach(kpi => {
    if (kpi.totalsDataSet?.values?.raw && kpi.dataLabels) {
      const labels = kpi.dataLabels.map(l => l.join(' '));
      labels.forEach((label, i) => {
        totals[kpi.yColumn.label] ??= {};
        totals[kpi.yColumn.label][label] = kpi.totalsDataSet?.values?.raw[i];
      });
    }
  });
  return totals;
};

const getKpisData = (raw: ScoreCardData[], grouping: string, periodLabels: string[]): KpisDataObject[] => {
  const baseKpi = raw.find(kpi => Array.isArray(kpi.dataSets) && kpi.dataSets.length > 0);
  const kpisData = [];
  if (baseKpi) {
    const groupingKey = grouping === 'prices' ? 'price_class' : 'brand';
    const kpisObjects = baseKpi.dataSets.map((ds, i) => {
      const kpisObj = { [groupingKey]: ds.label } as KpisDataObject;
      periodLabels.forEach((periodLabel, idx) => {
        raw.forEach(kpi => {
          if (kpi.dataSets?.[i]?.values?.raw) {
            kpisObj[`${kpi.yColumn.label}_${periodLabel}`] = kpi.dataSets[i].values.raw[idx];
          }
        });
      });
      return kpisObj;
    });
    kpisData.push(...kpisObjects);
  }
  return kpisData;
};

const getAverageSellingPrice = (raw: ScoreCardData[]): Array<Record<string, number | string>> | undefined => {
  let averagePrice = undefined;
  const avgKpi = raw.find(kpi => kpi.yColumn.name === 'average_price');
  if (avgKpi?.totalsDataSet?.values?.raw && avgKpi.dataLabels) {
    const avgPeriodLabels = avgKpi.dataLabels.map(l => l.join(' '));
    const marketTotal: Record<string, number | string> = {
      brand: 'Market Total',
    };
    avgPeriodLabels.forEach((label, idx) => {
      marketTotal[label] = avgKpi.totalsDataSet.values.raw[idx];
    });
    averagePrice = [marketTotal];
    averagePrice = averagePrice.concat(
      avgKpi.dataSets.map(ds => {
        const obj: Record<string, number | string> = { brand: ds.label };
        avgPeriodLabels.forEach((label, idx) => {
          obj[label] = ds.values.raw[idx];
        });
        return obj;
      })
    );
  }
  return averagePrice;
};

const mapFilterObject = (definitions: Record<string, FilterDefinition[]>, [key, val]): AppliedFilter | undefined => {
  const isFeatureSegment = /^f\d+_(id|value)$/i.test(key);
  const filterSection = isFeatureSegment ? 'Features' : 'Market';
  const filterDefinition = definitions[filterSection]?.find((definition: FilterDefinition) => definition.name === key);

  if (!filterDefinition) return;

  return {
    filter_label: isFeatureSegment ? 'Features Segments' : filterDefinition.label,
    filter_name: filterDefinition.label,
    filter_type: filterDefinition.type,
    filter_value: ['dateRange', 'numericRange'].includes(filterDefinition.type)
      ? val[0].label.split('-')
      : val.map((v: { label: string }) => v.label),
  };
};

const getAppliedFilters = (filtersContext: Filters): AppliedFilter[] | undefined => {
  if (!filtersContext) {
    return undefined;
  }
  // Map section labels to their definitions for easy lookup
  const sectionDefinitions = Object.fromEntries(
    filtersContext.sections.map(section => {
      const ungroupedDefs = section.ungrouped?.definitions || [];
      const groupedDefs = section.grouped?.length ? section.grouped.flatMap(group => group.definitions) : [];
      const definitions = [...ungroupedDefs, ...groupedDefs];

      return [section.label, definitions];
    })
  ) as Record<string, FilterDefinition[]>;

  const appliedFilters = Object.entries(filtersContext.values)
    .map(filterKeyVal => mapFilterObject(sectionDefinitions, filterKeyVal))
    .filter(Boolean);

  return appliedFilters.length ? appliedFilters : undefined;
};

const getDataGrouping = (grouping: string[]) => {
  const groupingStr = grouping.length ? grouping[0].split('|')[0] : '';
  let dataGrouping = '';

  if (groupingStr === 'brand_name') {
    dataGrouping = 'brand';
  } else if (groupingStr === 'segment_price') {
    dataGrouping = 'prices';
  }
  return dataGrouping;
};

/**
 * * This function is written for GenAI PoC to transform raw ScoreCard data into a structured format.
 * This is kind of a anti-pattern as ideally the backend should provide data in the required format.
 * However, given the time constraints and the PoC nature, this approach is taken.
 * In future, once the GenAI feature is stable and production-ready, this transformation logic
 * should be moved to the backend service (bff) to ensure consistency and reduce client-side processing.
 */
export async function transformScoreCard(
  raw: ScoreCardData[],
  grouping: string[],
  myBrand: string
): Promise<OutputStructure> {
  const contextInstance = ContextManager.getInstance().get();
  const contextValues = contextInstance.settings.values;
  const filters = getAppliedFilters(contextInstance.filters);
  const periodLabels = raw.find(kpi => kpi.dataLabels)?.dataLabels?.map(l => l.join(' ')) || [];
  let period = '';
  if (periodLabels.length > 1) {
    period = periodLabels.join(' vs ');
  }

  const dataGrouping = getDataGrouping(grouping);

  const chartId = `market_${dataGrouping}_scorecard`;
  const marketTotals = getMarketTotal(raw);
  const kpisData = getKpisData(raw, dataGrouping, periodLabels);
  const averageSellingPrice = getAverageSellingPrice(raw);

  const groupsData =
    dataGrouping === 'prices'
      ? { price_classes: kpisData as PricesObject[] }
      : {
          brands: kpisData as BrandObject[],
          my_brand: myBrand,
          focus_brand:
            Array.isArray(contextValues.highlightedBrand) && contextValues.highlightedBrand.length > 0
              ? contextValues.highlightedBrand[0].label
              : undefined,
        };

  return {
    chartId,
    proposition: contextValues.proposition[0].label,
    filters,
    chartData: {
      market: [contextValues.cell.map((c: { label: string }) => c.label).join(', ')],
      period,
      market_totals: marketTotals,
      currency:
        Array.isArray(contextValues.currency_code) && contextValues.currency_code.length > 0
          ? contextValues.currency_code[0].label
          : undefined,
      ...groupsData,
      ...(averageSellingPrice ? { average_selling_price: averageSellingPrice } : {}),
    },
    contextualData: {},
  };
}
